import axios from 'axios'
import {
  getAnonymousId,
  removeItemFromStorage,
  setDataInStorage,
} from 'utils/storage'
import {isViewOnlyError, notifyViewOnly} from 'utils/viewOnly'

const proxy = process.env.REACT_APP_STAGING_API_URL

// A stored session can stop being valid without the app noticing: the token
// expires, the session row is destroyed, or — the common one in development —
// the stored session belongs to a user that doesn't exist in whatever backend
// REACT_APP_STAGING_API_URL now points at. Every authenticated call then 401s,
// and because callers only ever see a rejected promise, the failure surfaces as
// whatever generic message that caller happens to use ("Failed to save. Please
// try again."), leaving the user stuck on a page they cannot complete.
//
// Treat a 401 as what it is — this session is over — and send them to the login
// screen with the stale copy cleared.
//
// Two cases must NOT trigger it. `/login` itself, because a wrong password is a
// 401 the login form has to render inline. And any 401 taken while logged out:
// several public pages call authenticated endpoints speculatively (a visitor
// opening a public profile still asks for its video resume), and those get a
// 401 by design. Only a request made *with* a stored session says anything
// about that session's validity.
const LOGIN_PATH = '/login'

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status
    const requestUrl = error?.config?.url || ''
    const isLoginRequest = requestUrl.includes(LOGIN_PATH)
    const hadSession = Boolean(getToken()?.token)

    // Investor accounts are view-only: one notice for every refused write,
    // and callers can check error.isViewOnly to skip their own failure UI.
    if (isViewOnlyError(error)) {
      error.isViewOnly = true
      notifyViewOnly()
    }

    if (status === 401 && hadSession && !isLoginRequest) {
      removeItemFromStorage({name: 'user'})
      removeItemFromStorage({name: 'profile'})
      if (window.location.pathname !== LOGIN_PATH) {
        window.location.assign(`${LOGIN_PATH}?expired=1`)
      }
    }
    return Promise.reject(error)
  }
)

export const getToken = () => JSON.parse(localStorage.getItem('user') || '{}')

// Public feature flags (e.g. coinHeaderEnabled). No auth; admins flip
// them on the Admin → Setup page.
export const getAppSettings = async () => {
  const response = await axios(proxy + '/app-settings', {method: 'GET'})
  return response?.data || {}
}

export const registerUser = async (data) => {
  const response = await axios(proxy + '/register', {
    method: 'POST',
    data: data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const verifyUser = async (verificationCode) => {
  const response = await axios(
    proxy + `/verifyEmail?token=${verificationCode}`,
    {
      method: 'GET',
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const loginUser = async (data, tokens) => {
  const {token} = getToken()
  const response = await axios(proxy + '/login', {
    method: 'POST',
    headers: {token: tokens || token},
    data: data,
  })
  if (response && response.data) {
    setDataInStorage({name: 'user', data: response.data})
    return response.data
  }
  return null
}

export const forgotPassword = async (data) => {
  const response = await axios(proxy + '/forgotPassword', {
    method: 'POST',
    data: data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const resetPassword = async (data) => {
  const response = await axios(proxy + `/resetPassword`, {
    method: 'POST',
    data: data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const resendVerification = async (data) => {
  const response = await axios(proxy + `/verifyEmail`, {
    method: 'POST',
    data: data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getMyProfile = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/users/${id}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    setDataInStorage({
      name: 'profile',
      data: response.data,
    })
    return response.data
  }
  return null
}

export const logoutUser = async ({sessionId, token}) => {
  const response = await axios(proxy + '/logout', {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      sessionId,
    },
  })
  if (response && response.data) {
    removeItemFromStorage({name: 'user'})
    return response.data
  }
  return null
}

export const completeProfile = async (data) => {
  const {token} = getToken()
  const response = await axios(proxy + '/users', {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const completeOnboarding = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/users/onboarding-complete', {
    method: 'PUT',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}

export const uploadFile = async (data) => {
  const {token} = getToken()
  const response = await axios(proxy + `/medias/profile`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
    data: data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

/**
 * Unified media fetching function for all entity types and media types.
 * This is the recommended function to use for all new code.
 *
 * @param {Object} params - Parameters object
 * @param {'user' | 'business'} params.entityType - Type of entity (user or business)
 * @param {string} params.entityId - ID of the entity
 * @param {'avatar' | 'logo'} params.mediaType - Type of media (avatar or logo)
 * @param {number} [params.size] - Optional size for width and height
 * @param {boolean} [params.excludeDefault] - Return null instead of the
 *   server's generic default-<type>.png placeholder
 * @returns {Promise<string|null>} Blob URL of the media or null if not found
 *
 * @example
 * // Fetch user avatar
 * getMedia({ entityType: 'user', entityId: userId, mediaType: 'avatar', size: 320 })
 *
 * @example
 * // Fetch business logo
 * getMedia({ entityType: 'business', entityId: businessId, mediaType: 'logo', size: 160 })
 */
export const getMedia = async ({
  entityType,
  entityId,
  mediaType,
  excludeDefault = false,
}) => {
  // Build the appropriate endpoint based on entity type
  const endpoint =
    entityType === 'business'
      ? `/medias/business/${entityId}/${mediaType}`
      : `/medias/${mediaType}/${entityId}`

  const url = proxy + endpoint

  try {
    const response = await axios(url, {
      method: 'GET',
      responseType: 'blob',
    })
    if (response && response.data) {
      // When the entity has no uploaded media, the server substitutes a
      // generic default-<type>.png. Those responses carry no x-id header
      // (it's only set from a real profile_medias row), which lets callers
      // opt out of the grey placeholder and fall back to something better.
      if (excludeDefault && !response.headers?.['x-id']) {
        return null
      }
      return URL.createObjectURL(response.data)
    }
    return null
  } catch (err) {
    // A 404 here means the entity simply has no uploaded media of this
    // kind (common for users without an avatar, businesses without a
    // logo, etc.). Treat it the same as an empty response. We still
    // re-throw on other statuses so real errors (auth, 5xx, network)
    // stay visible rather than getting swallowed silently.
    const status = err?.response?.status
    if (status === 404 || status === 403) {
      return null
    }
    throw err
  }
}

// ============================================================================
// LEGACY WRAPPER FUNCTIONS (backwards compatibility)
// These functions are maintained for backwards compatibility.
// For new code, prefer using getMedia() directly.
// See CLAUDE.md for migration guidelines.
// ============================================================================

export const getAvatar = async (id, size) => {
  return getMedia({entityType: 'user', entityId: id, mediaType: 'avatar', size})
}

export const getPublicAvatar = async (id, size) => {
  return getMedia({entityType: 'user', entityId: id, mediaType: 'avatar', size})
}

export const getBusinessImg = async (id) => {
  return getMedia({entityType: 'user', entityId: id, mediaType: 'logo'})
}

export const getPublicBusinessImg = async (id) => {
  return getMedia({entityType: 'business', entityId: id, mediaType: 'logo'})
}

export const getBusinessLogoByBusinessId = async (businessId) => {
  return getMedia({
    entityType: 'business',
    entityId: businessId,
    mediaType: 'logo',
  })
}

export const getBusinessAvatarByBusinessId = async (businessId, size) => {
  return getMedia({
    entityType: 'business',
    entityId: businessId,
    mediaType: 'avatar',
    size,
  })
}

// ============================================================================
// VIDEO RESUME FUNCTIONS
// Functions for handling video resume uploads, retrieval, and deletion
// ============================================================================

/**
 * Get user's video resume
 * @param {string} userId - ID of the user
 * @returns {Promise<string|null>} Blob URL of the video resume or null if not found
 */
// The reel is public, but sending `Authorization: Bearer undefined` for a
// signed-out visitor makes the API reject the request outright (401) instead of
// serving it anonymously — so a visitor saw no reel at all. Send the header only
// when there is really a token.
export const getVideoResume = async (userId) => {
  const {token} = getToken()
  try {
    const response = await axios(proxy + `/medias/video-resume/${userId}`, {
      method: 'GET',
      headers: token ? {Authorization: `Bearer ${token}`} : undefined,
      responseType: 'blob',
    })
    if (response && response.data) {
      const videoUrl = URL.createObjectURL(response.data)
      const metadata = {
        id: response.headers['x-id'],
        duration: response.headers['x-duration']
          ? Number(response.headers['x-duration'])
          : null,
        size: response.headers['x-size']
          ? Number(response.headers['x-size'])
          : null,
        createdTimestamp: response.headers['x-createdtimestamp'] || null,
      }
      return {videoUrl, metadata}
    }
  } catch (error) {
    // Return null if no video resume found
    return null
  }
  return null
}

/**
 * Get a business's intro video (stored as a `video_resume` scoped to the business).
 * Mirrors {@link getVideoResume} but hits the business-scoped route and reads the
 * same `x-id` / `x-duration` / `x-size` / `x-createdtimestamp` response headers so
 * the hero player can show metadata consistently for both job-seekers and businesses.
 *
 * @param {string} businessId
 * @returns {Promise<{videoUrl: string, metadata: object}|null>}
 */
export const getBusinessVideoResume = async (businessId) => {
  const {token} = getToken()
  try {
    const response = await axios(
      proxy + `/medias/business/${businessId}/video-resume`,
      {
        method: 'GET',
        // Same signed-out 401 as getVideoResume above.
        headers: token ? {Authorization: `Bearer ${token}`} : undefined,
        responseType: 'blob',
      }
    )
    if (response && response.data) {
      const videoUrl = URL.createObjectURL(response.data)
      const metadata = {
        id: response.headers['x-id'],
        duration: response.headers['x-duration']
          ? Number(response.headers['x-duration'])
          : null,
        size: response.headers['x-size']
          ? Number(response.headers['x-size'])
          : null,
        createdTimestamp: response.headers['x-createdtimestamp'] || null,
      }
      return {videoUrl, metadata}
    }
  } catch (error) {
    return null
  }
  return null
}

/**
 * Upload a video resume for a user
 * @param {File} videoFile - The video file to upload
 * @param {string} [targetUserId] - Optional: User ID to upload for (admin only)
 * @param {number} [duration] - Optional: video duration in seconds (lets the
 *   backend enforce the 3-minute limit and serve duration metadata)
 * @returns {Promise<Object>} Response with media ID
 */
export const uploadVideoResume = async (
  videoFile,
  targetUserId = null,
  duration = null
) => {
  const formData = new FormData()
  formData.append('file', videoFile)
  formData.append('type', 'video_resume')
  formData.append('mediaType', 'video')

  if (targetUserId) {
    formData.append('targetUserId', targetUserId)
  }
  if (duration && Number.isFinite(duration)) {
    formData.append('duration', Math.round(duration))
  }

  return uploadFile(formData)
}

/**
 * Delete a video resume
 * @param {string} mediaId - ID of the media to delete
 * @returns {Promise<Object>} Response data
 */
export const deleteVideoResume = async (mediaId) => {
  const {token} = getToken()
  const response = await axios(proxy + `/medias/${mediaId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getPortfolioInfo = async (id, businessId) => {
  let url = proxy + `/users/${id}/portfolio`
  if (businessId !== undefined) {
    url += `?businessId=${businessId || ''}`
  }
  const response = await axios(url, {
    method: 'GET',
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

// Sends the session token when there is one: the API keeps resumes, cover
// letters and documents behind an ownership/admin check (403 otherwise), and
// an anonymous request can never pass it. Public media types ignore the header.
export const getFile = async ({id, type}) => {
  const url = proxy + `/medias/profile/${id}?type=${type}`
  const {token} = getToken()
  const response = await axios(url, {
    method: 'GET',
    responseType: 'blob',
    headers: token ? {Authorization: `Bearer ${token}`} : undefined,
  })

  if (response && response.data) {
    const fileType = response.headers.get('x-mediaType')
    let file = response.data
    if (fileType === 'pdf')
      file = new Blob([response.data], {type: 'application/pdf'})
    const fileURL = URL.createObjectURL(file)
    return {
      url: fileURL,
      specs: {
        type: fileType,
        width: response.headers.get('x-width'),
        height: response.headers.get('x-height'),
        size: response.headers.get('x-size'),
        duration: response.headers.get('x-duration'),
        ext: response.headers.get('x-ext'),
      },
    }
  }
  return null
}

export const getUsers = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/users', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {users, totalCount} = response.data
    return {users: users, totalCount}
  }
  return null
}

export const addToFavorites = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/favorites/users/${id}/add`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const removeFromFavorites = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/favorites/users/${id}/remove`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

// Business favorites (parallel to user favorites)
export const addBusinessToFavorites = async (businessId) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/favorites/businesses/${businessId}/add`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const removeBusinessFromFavorites = async (businessId) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/favorites/businesses/${businessId}/remove`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const deleteFromPortfolio = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/medias/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getUser = async (id) => {
  const response = await axios(proxy + `/users/${id}`, {
    method: 'GET',
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getLoggedInViewUser = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/users/${id}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const initializeJobPost = async (businessId) => {
  const {token} = getToken()
  const response = await axios(proxy + '/jobs/posts', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: businessId ? {businessId} : {},
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const createJobPost = async ({id, jobData}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/jobs/posts/${id}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: jobData,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getCreatedJobPosts = async (
  pageNumber,
  size = 10,
  status,
  type,
  businessId
) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  let url = proxy + `/jobs/posts?from=${from + 1}&size=${size}`
  if (status && status !== 'all') url += `&status=${status}`
  if (type && type !== 'all') url += `&type=${type}`
  if (businessId) url += `&businessId=${businessId}`
  const response = await axios(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {jobPosts, totalCount} = response.data
    return {jobPosts, totalCount}
  }
  return null
}

export const getJobPosts = async (pageNumber, size = 10, title) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  let url = proxy + `/jobs/posts/active/all?from=${from + 1}&size=${size}`
  if (title && title !== 'all') url += `&title=${title}`
  const response = await axios(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {jobPosts, totalCount} = response.data
    return {jobPosts, totalCount}
  }
  return null
}

export const getLatestJobPosts = async () => {
  const {token} = getToken()
  let url = proxy + `/jobs/posts/active/latest`
  const response = await axios(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {jobPosts, totalCount} = response.data
    return {jobPosts, totalCount}
  }
  return null
}

export const getJobPost = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/jobs/posts/${id}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getJobPostPublic = async (id) => {
  const response = await axios(proxy + `/jobs/posts/${id}`, {
    method: 'GET',
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

// `answers` = [{jobPostQuestionId, answer}] for text questions or
// [{jobPostQuestionId, answerMediaId}] for video ones. Callers that only
// have an id (one-tap apply) can still pass it alone.
export const applyForJob = async (arg) => {
  const {jobId, answers} = typeof arg === 'object' ? arg : {jobId: arg}
  const {token} = getToken()
  const response = await axios(proxy + `/jobs/posts/${jobId}/applications`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: answers?.length ? {answers} : {},
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

// One application with the applicant's answers (applicant or the job's business).
export const getJobApplicationDetail = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/jobs/posts/applications/${id}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response?.data ?? null
}

// "Invite to apply": the viewer's open jobs and where this person stands on each.
export const getInviteOptions = async (userId) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/jobs/invites/options?userId=${encodeURIComponent(userId)}`,
    {method: 'GET', headers: {Authorization: `Bearer ${token}`}}
  )
  return response?.data?.jobs ?? []
}

export const inviteToApply = async ({jobPostId, userId}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/jobs/posts/${jobPostId}/invites`, {
    method: 'POST',
    headers: {Authorization: `Bearer ${token}`},
    data: {userId},
  })
  return response?.data ?? null
}

// The business hires an applicant it moved forward with (fills an opening).
export const hireApplication = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/jobs/posts/applications/${id}/hire`, {
    method: 'POST',
    headers: {Authorization: `Bearer ${token}`},
  })
  return response?.data ?? null
}

// The applicant pulls their application.
export const withdrawApplication = async (id) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/jobs/posts/applications/${id}/withdraw`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return response?.data ?? null
}

// Interview scheduling inside an application thread.
export const getInterviews = async (applicationId) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/jobs/posts/applications/${applicationId}/interviews`,
    {method: 'GET', headers: {Authorization: `Bearer ${token}`}}
  )
  return response?.data?.proposals ?? []
}

// format: 'in_person' | 'phone' | 'video'; slots: 1-3 ISO times
export const proposeInterview = async ({applicationId, ...data}) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/jobs/posts/applications/${applicationId}/interviews`,
    {method: 'POST', headers: {Authorization: `Bearer ${token}`}, data}
  )
  return response?.data ?? null
}

// {action: 'accept', slot} or {action: 'decline', note}
export const respondToInterview = async ({id, ...data}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/jobs/interviews/${id}`, {
    method: 'PUT',
    headers: {Authorization: `Bearer ${token}`},
    data,
  })
  return response?.data ?? null
}

// Replaces a job's application questions: [{name, answerKind, required}].
export const setJobPostQuestions = async ({id, questions}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/jobs/posts/${id}/questions`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {questions},
  })
  return response?.data ?? null
}

export const extendJobPost = async ({id, days}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/jobs/posts/${id}/extend`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: days ? {days} : {},
  })
  return response?.data ?? null
}

// reason: 'filled' | 'no_longer_hiring' | 'other'
export const closeJobPost = async ({id, reason}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/jobs/posts/${id}/close`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {reason},
  })
  return response?.data ?? null
}

export const getJobApplications = async (id, pageNumber, size) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  const response = await axios(
    proxy + `/jobs/posts/${id}/applications?from=${from + 1}&size=${size}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    const {applications, totalCount} = response.data
    return {applications, totalCount}
  }
  return null
}

export const getFavoriteApplicants = async (pageNumber, size) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  const response = await axios(
    proxy + `/favorites/users?from=${from + 1}&size=${size}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    const {favorites, totalCount} = response.data
    return {favorites, totalCount}
  }
  return null
}

export const getFavoriteBusinesses = async (pageNumber, size) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  const response = await axios(
    proxy +
      `/favorites/users?from=${from + 1}&size=${size}&role=business_owner`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    const {favorites, totalCount} = response.data
    return {favorites, totalCount}
  }
  return null
}

export const getFavoriteProfessionals = async (pageNumber, size) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  const response = await axios(
    proxy + `/favorites/users?from=${from + 1}&size=${size}&role=job_seeker`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    const {favorites, totalCount} = response.data
    return {favorites, totalCount}
  }
  return null
}

export const acceptApplication = async (id) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/jobs/posts/applications/${id}/accept`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const rejectApplication = async (id) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/jobs/posts/applications/${id}/reject`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getAppliedJobs = async (pageNumber, size = 10) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  let url = proxy + `/jobs/applications/applied?from=${from + 1}&size=${size}`
  const response = await axios(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {applications, totalCount} = response.data
    return {applications, totalCount}
  }
  return null
}

export const getSavedJobPosts = async (pageNumber, size = 10) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  let url = proxy + `/favorites/jobs?from=${from + 1}&size=${size}`
  const response = await axios(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {favorites, totalCount} = response.data
    return {favorites, totalCount}
  }
  return null
}

export const saveJobPost = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/favorites/jobs/${id}/add`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const unsaveJobPost = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/favorites/jobs/${id}/remove`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getNotificationsCount = async () => {
  const {token} = getToken()
  const response = await axios(proxy + `/notifications/count`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {unreadCount, totalCount} = response.data
    return {unreadCount, totalCount}
  }
  return null
}

export const getNotifications = async (pageNumber = 1, size = 10) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  const response = await axios(
    proxy + `/notifications?from=${from + 1}&size=${size}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    const {notifications, totalCount} = response.data
    return {notifications, totalCount}
  }
  return null
}

export const markNotificationAsRead = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/notifications/${id}/read`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const markAllNotificationsAsRead = async () => {
  const {token} = getToken()
  const response = await axios(proxy + `/notifications/read`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getGlobalAlerts = async () => {
  const {token} = getToken()
  const response = await axios(proxy + `/global-alerts`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {alerts} = response.data
    return {alerts}
  }
  return null
}

export const dismissGlobalAlert = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/global-alerts/${id}/dismiss`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const createConversation = async (data) => {
  const {token} = getToken()
  const response = await axios(proxy + '/conversations', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

// Investors only: the SHARPENN team (admins), the people they can message.
export const getTeamContacts = async () => {
  const {token} = getToken()
  const response = await axios(proxy + `/conversations/team`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response?.data || []
}

export const getConversations = async () => {
  const {token} = getToken()
  const response = await axios(proxy + `/conversations`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getConversation = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/conversations/${id}/message`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const markConversationAsRead = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/conversations/${id}/read`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const deleteConversation = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/conversations/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const sendMessage = async ({id, data}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/conversations/${id}/message`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

// --- Circles (group conversations) ----------------------------------------
// Same conversations backbone as DMs; a Circle is a conversation with
// type='group'. createConversation above already accepts {type, name,
// description, users} — these cover the group-specific endpoints.

export const getConversationMessages = async ({id, before, limit = 50}) => {
  const {token} = getToken()
  const params = new URLSearchParams()
  if (before) params.set('before', before)
  params.set('limit', limit)
  const response = await axios(
    proxy + `/conversations/${id}/message?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const updateConversation = async ({id, data}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/conversations/${id}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getConversationMembers = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/conversations/${id}/members`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const addConversationMembers = async ({id, users}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/conversations/${id}/members`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {users},
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const removeConversationMember = async ({id, userId}) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/conversations/${id}/members/${userId}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const updateConversationMemberRole = async ({id, userId, role}) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/conversations/${id}/members/${userId}/role`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: {role},
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const createConversationInvite = async ({id, data}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/conversations/${id}/invites`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: data || {},
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

// Public — the join page renders this before the visitor has an account.
export const getConversationInvite = async (inviteToken) => {
  const response = await axios(
    proxy + `/conversations/invites/${inviteToken}`,
    {
      method: 'GET',
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const joinConversationInvite = async (inviteToken) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/conversations/invites/${inviteToken}/join`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const editConversationMessage = async ({id, messageId, content}) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/conversations/${id}/message/${messageId}`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: {content},
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const deleteConversationMessage = async ({id, messageId}) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/conversations/${id}/message/${messageId}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const addMessageReaction = async ({id, messageId, emoji}) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/conversations/${id}/message/${messageId}/reaction`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: {emoji},
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const removeMessageReaction = async ({id, messageId, emoji}) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/conversations/${id}/message/${messageId}/reaction`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: {emoji},
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const addJobPosition = async (data) => {
  const {token} = getToken()
  const response = await axios(proxy + `/experiences`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const updateJobPosition = async ({id, data}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/experiences/${id}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const deleteJobPosition = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/experiences/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const updatePassword = async (data) => {
  const {token} = getToken()
  const response = await axios(proxy + '/users/updatePassword', {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const deactivateUser = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/users', {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const deleteJobPost = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/jobs/posts/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getAllUsers = async (
  pageNumber,
  size = 10,
  searchTerm,
  profession,
  role,
  businessType
) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  let url = proxy + `/users?from=${from + 1}&size=${size}`
  if (role) url += `&role=${role}`
  if (searchTerm) url += `&search=${searchTerm}`
  if (profession) url += `&profession=${profession}`
  if (businessType) url += `&businessType=${businessType}`
  const response = await axios(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {users, totalCount} = response.data
    return {users: users, totalCount}
  }
  return null
}

export const getAllBusinesses = async ({
  pageNumber = 1,
  size = 10,
  type,
  location,
  services,
  search,
}) => {
  const {token} = getToken()
  // Unlike /users and the jobs endpoints (1-based from/size), /businesses
  // paginates with limit/offset — from/size get silently ignored and every
  // page returns the same first rows.
  const offset = (pageNumber - 1) * size
  let url = proxy + `/businesses?limit=${size}&offset=${offset}`
  if (type) url += `&type=${type}`
  if (location) url += `&location=${encodeURIComponent(location)}`
  if (search) url += `&search=${encodeURIComponent(search)}`
  if (services && services.length) {
    const servicesParam = services.map((s) => encodeURIComponent(s)).join(',')
    url += `&services=${servicesParam}`
  }

  const response = await axios(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (response && response.data) {
    const {businesses, total} = response.data
    return {businesses, totalCount: total}
  }
  return null
}

export const searchJobPosts = async (
  pageNumber,
  size = 10,
  searchTerm,
  employmentType,
  visitor,
  title
) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  let url = proxy + `/jobs/posts/active/all?from=${from + 1}&size=${size}`
  if (searchTerm) url += `&search=${searchTerm}`
  if (employmentType) url += `&employmentType=${employmentType}`
  if (title) url += `&title=${title}`

  const config = {
    method: 'GET',
    ...(visitor ? {} : {headers: {Authorization: `Bearer ${token}`}}),
  }

  const response = await axios(url, config)
  if (response && response.data) {
    const {jobPosts, totalCount} = response.data
    return {jobPosts, totalCount}
  }
  return null
}

export const subscribe = async (data) => {
  const response = await axios(proxy + `/waitlist`, {
    method: 'POST',
    data: data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getConversationsCount = async () => {
  const {token} = getToken()
  const response = await axios(proxy + `/conversations/count`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {unreadCount, waitingCount} = response.data
    return {unreadCount, waitingCount}
  }
  return null
}

export const facebookLogin = async (data, tokens) => {
  const {token} = getToken()
  const response = await axios(proxy + '/facebookSignIn', {
    method: 'POST',
    headers: {token: tokens || token},
    data: data,
  })
  if (response && response.data) {
    setDataInStorage({name: 'user', data: response.data})
    return response.data
  }
  return null
}

export const getCertificatesInfo = async (id) => {
  const response = await axios(proxy + `/users/${id}/certificate`, {
    method: 'GET',
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getAllProfessionals = async (
  pageNumber,
  size = 10,
  searchTerm,
  profession
) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  let url = proxy + `/users?from=${from + 1}&size=${size}&role=job_seeker`
  if (searchTerm) url += `&search=${searchTerm}`
  if (profession) url += `&profession=${profession}`
  const response = await axios(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {users, totalCount} = response.data
    return {users: users, totalCount}
  }
  return null
}

export const addRecommendation = async (data) => {
  const {token} = getToken()
  const response = await axios(proxy + `/recommendations`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getRecommendations = async (id, pageNumber, size = 1) => {
  const from = pageNumber * size - size
  let url = proxy + `/recommendations/${id}?from=${from + 1}&size=${size}`
  const response = await axios(url, {
    method: 'GET',
  })
  if (response && response.data) {
    const {recommendations, totalCount} = response.data
    return {recommendations: recommendations, totalCount}
  }
  return null
}

export const deleteRecommendation = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/recommendations/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const editPortfolioDescription = async ({fileId, description}) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/medias/profile/${fileId}/description`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: {description},
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getPortfolioInfoPrivate = async (id, businessId) => {
  const {token} = getToken()
  let url = proxy + `/users/${id}/portfolio`
  if (businessId !== undefined) {
    url += `?businessId=${businessId || ''}`
  }
  const response = await axios(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const likeFile = async (id, {businessId = null} = {}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/medias/profile/${id}/likes`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {businessId},
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const unlikeFile = async (id, {businessId = null} = {}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/medias/profile/${id}/likes`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {businessId},
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const initializeClass = async (businessId) => {
  const {token} = getToken()
  const response = await axios(proxy + '/classes/posts', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: businessId ? {businessId} : {},
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const createClass = async ({id, classData}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/classes/posts/${id}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: classData,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const uploadPromoImg = async ({formData}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/medias/class`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getAllClasses = async (
  pageNumber,
  size = 10,
  status,
  businessId
) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  let url = proxy + `/classes/posts?from=${from + 1}&size=${size}`
  if (status) url += `&status=${status}`
  if (businessId) url += `&businessId=${businessId}`
  const response = await axios(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {classPosts, totalCount} = response.data
    return {classPosts, totalCount}
  }
  return null
}

export const getClassPost = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/classes/posts/${id}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getClassLogo = async (id, size) => {
  let url = proxy + `/medias/classes/logo/${id}`
  if (size) url += `type=logo?width=${size}&height=${size}`
  const response = await axios(url, {
    method: 'GET',
    responseType: 'blob',
  })
  if (response && response.data) {
    return URL.createObjectURL(response.data)
  }
  return null
}

export const getClassFile = async (id, size) => {
  let url = proxy + `/medias/class/${id}?type=logo`
  if (size) url += `&width=${size}&height=${size}`
  const response = await axios(url, {
    method: 'GET',
    responseType: 'blob',
  })
  if (response && response.data) {
    return URL.createObjectURL(response.data)
  }
  return null
}

export const getClassApplications = async (id, pageNumber, size) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  const response = await axios(
    proxy + `/classes/posts/${id}/applications?from=${from + 1}&size=${size}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    const {applications, totalCount} = response.data
    return {applications, totalCount}
  }
  return null
}

export const getLatestClassPosts = async () => {
  const {token} = getToken()
  let url = proxy + `/classes/posts/active/latest`
  const response = await axios(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {classPosts, totalCount} = response.data
    return {classPosts, totalCount}
  }
  return null
}

export const getClassPosts = async (pageNumber, size = 10, title) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  let url = proxy + `/classes/posts/active/all?from=${from + 1}&size=${size}`
  if (title && title !== 'all') url += `&category=${title}`
  const response = await axios(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {classPosts, totalCount} = response.data
    return {classPosts, totalCount}
  }
  return null
}

export const signUpForClass = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/classes/posts/${id}/applications`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const cancelClassApplication = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/classes/posts/${id}/applications`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const searchClassPosts = async (
  pageNumber,
  size = 10,
  searchTerm,
  category
) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  let url = proxy + `/classes/posts/active/all?from=${from + 1}&size=${size}`
  if (searchTerm) url += `&search=${searchTerm}`
  if (category) url += `&category=${category}`
  const response = await axios(url, {
    method: 'GET',
    headers: token ? {Authorization: `Bearer ${token}`} : {},
  })
  if (response && response.data) {
    const {classPosts, totalCount} = response.data
    return {classPosts, totalCount}
  }
  return null
}

export const getAppliedClasses = async (pageNumber, size = 10) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  let url =
    proxy + `/classes/applications/applied?from=${from + 1}&size=${size}`
  const response = await axios(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {applications, totalCount} = response.data
    return {applications, totalCount}
  }
  return null
}

export const getClassApplicants = async (id, pageNumber, size) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  const response = await axios(
    proxy + `/classes/posts/${id}/participants?from=${from + 1}&size=${size}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    const {participants, totalCount} = response.data
    return {participants, totalCount}
  }
  return null
}

export const getClassReviews = async (id, pageNumber, size) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  const response = await axios(
    proxy + `/reviews/${id}?from=${from + 1}&size=${size}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    const {reviews, totalCount, totalRating} = response.data
    return {reviews, totalCount, totalRating}
  }
  return null
}

export const saveCustomMessage = async ({id, message}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/classes/posts/${id}/message`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {message},
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const selectAttendees = async ({id, participants}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/classes/posts/${id}/applications`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {participants},
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const removeReview = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/reviews/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const postReview = async (data) => {
  const {token} = getToken()
  const response = await axios(proxy + '/reviews', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getExistingCertificatesInfo = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/classes/posts/${id}/certificate`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getClassCertificateFile = async ({id, type, size}) => {
  let url = proxy + `/medias/class/${id}?type=${type}`
  if (size) url += `&width=${size}&height=${size}`
  const response = await axios(url, {
    method: 'GET',
    responseType: 'blob',
  })

  if (response && response.data) {
    const fileType = response.headers.get('x-mediaType')
    let file = response.data
    if (fileType === 'pdf')
      file = new Blob([response.data], {type: 'application/pdf'})
    const fileURL = URL.createObjectURL(file)
    return {
      url: fileURL,
      specs: {
        type: fileType,
        width: response.headers.get('x-width'),
        height: response.headers.get('x-height'),
        size: response.headers.get('x-size'),
        duration: response.headers.get('x-duration'),
        ext: response.headers.get('x-ext'),
      },
    }
  }
  return null
}

export const getClassPhotosInfoPrivate = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/classes/posts/${id}/portfolio`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getClassPhotosInfo = async (id) => {
  const response = await axios(proxy + `/classes/posts/${id}/portfolio`, {
    method: 'GET',
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const searchClassApplicants = async (id, pageNumber, size, search) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  const response = await axios(
    proxy +
      `/classes/posts/${id}/participants?from=${from + 1}&size=${size}&search=${search}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    const {participants, totalCount} = response.data
    return {participants, totalCount}
  }
  return null
}

export const getProfiles = async (pageNumber, size = 10, role, searchTerm) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  let url = proxy + `/users/invite/search?from=${from + 1}&size=${size}`
  if (role) url += `&role=${role}`
  if (searchTerm) url += `&search=${searchTerm}`
  const response = await axios(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {users, totalCount} = response.data
    return {users: users, totalCount}
  }
  return null
}

// Whether (and how much) the invitee_signup reward pays the inviter —
// drives the "you earn X Sharpennies" line on the invite page.
export const getInviteReward = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/invite/reward', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const inviteUser = async (data) => {
  const {token} = getToken()
  const response = await axios(proxy + '/invite', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {data: data},
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const setEmployer = async (businessId) => {
  const {token} = getToken()
  const response = await axios(proxy + '/users/employer', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    data: {businessId},
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const removeEmployer = async (businessId) => {
  const {token} = getToken()
  const response = await axios(proxy + `/users/employer?id=${businessId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const removeFromTeam = async (userId, businessId) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/users/employees?id=${userId}&businessId=${businessId}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const removeSelfFromTeam = async (businessId) => {
  const {token} = getToken()
  const response = await axios(proxy + `/users/employer?id=${businessId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getStudiosProfessionals = async ({
  pageNumber,
  size = 10,
  searchTerm,
  profession,
  role,
  businessType,
}) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  let url = proxy + `/consumers/services?from=${from + 1}&size=${size}`
  if (role) url += `&role=${role}`
  if (searchTerm) url += `&search=${searchTerm}`
  if (profession) url += `&profession=${profession}`
  if (businessType) url += `&businessType=${businessType}`
  const response = await axios(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {services, totalCount} = response.data
    return {services: services, totalCount}
  }
  return null
}

export const purchaseCredits = async (data) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `${process.env.REACT_APP_PURCHASE_CREDITS_ROUTE}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: data,
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const initializePayment = async (id) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `${process.env.REACT_APP_INITIALIZE_PAYMENT_ROUTE}/${id}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const activatePremium = async (data) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `${process.env.REACT_APP_ACTIVATE_PREMIUM_ROUTE}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: data,
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getClassPostPublic = async (id) => {
  const response = await axios(proxy + `/classes/posts/${id}`, {
    method: 'GET',
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getTrendingPosts = async (pageNumber, size) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  const response = await axios(
    proxy + `/trending/portfolios?from=${from + 1}&size=${size}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    const {trending, totalCount} = response.data
    return {trending, totalCount}
  }
  return null
}

// ---------------------------------------------------------------------------
// Spaces (rentable business real estate — chair, room, booth, studio, etc.)
// ---------------------------------------------------------------------------

/**
 * Allocate a draft space_post for a business. The composer flow calls
 * this first, then updates the draft with the real form body.
 */
export const initializeSpace = async (businessId) => {
  const {token} = getToken()
  const response = await axios(proxy + '/spaces/posts', {
    method: 'POST',
    headers: {Authorization: `Bearer ${token}`},
    data: businessId ? {businessId} : {},
  })
  if (response && response.data) return response.data
  return null
}

export const updateSpace = async ({id, spaceData}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/spaces/posts/${id}`, {
    method: 'PUT',
    headers: {Authorization: `Bearer ${token}`},
    data: spaceData,
  })
  if (response && response.data) return response.data
  return null
}

/**
 * Upload a single photo or video attached to an existing space_post.
 * Mirrors uploadFile (multipart) and uploadPromoImg in shape — the
 * caller builds the FormData with `file`, `spacePostId`, `mediaType`,
 * and `type` (use `post_media` for composer-driven space gallery
 * uploads to land them in the post-media bucket alongside other
 * composer media).
 */
export const uploadSpaceMedia = async ({formData}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/medias/space`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getSpace = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/spaces/posts/${id}`, {
    method: 'GET',
    headers: token ? {Authorization: `Bearer ${token}`} : {},
  })
  if (response && response.data) return response.data
  return null
}

export const deleteSpace = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/spaces/posts/${id}`, {
    method: 'DELETE',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}

/**
 * Paginated browse for the /spaces discovery tab. Shape mirrors the
 * existing searchJobPosts / searchClassPosts return so BrowseList can
 * consume it without special-casing.
 */
export const searchSpaces = async (
  pageNumber,
  size = 10,
  searchTerm,
  spaceType,
  eligibleJobTypes
) => {
  const {token} = getToken()
  const from = pageNumber * size - size + 1
  const params = new URLSearchParams({from: String(from), size: String(size)})
  if (searchTerm) params.set('search', searchTerm)
  if (spaceType) params.set('spaceType', spaceType)
  // CSV form keeps the URL short and matches the backend's accepted
  // shapes (CSV or repeated). See B2:
  // backend/lib/endpoints/spaces/space-posts/get-space-posts-active/
  // get-space-posts-active-request-validation.js
  if (Array.isArray(eligibleJobTypes) && eligibleJobTypes.length > 0) {
    params.set('eligibleJobTypes', eligibleJobTypes.join(','))
  }
  const response = await axios(
    proxy + `/spaces/posts/active/all?${params.toString()}`,
    {
      method: 'GET',
      headers: token ? {Authorization: `Bearer ${token}`} : {},
    }
  )
  if (response && response.data) {
    const {items, totalCount} = response.data
    return {items, totalCount}
  }
  return {items: [], totalCount: 0}
}

// ---------------------------------------------------------------------------
// Map
// ---------------------------------------------------------------------------

/**
 * Fetch businesses + their child listings (spaces / jobs / classes) inside
 * a viewport bounding box. The /map page calls this on mount and on every
 * "Search this area" press.
 *
 * Mirrors the backend validator in
 * `lib/endpoints/map/get-map/get-map-request-validation.js`:
 *
 *   bbox            { swLat, swLng, neLat, neLng }
 *   kinds           string[]   (subset of ['spaces','jobs','classes'])
 *   center          { lat, lng } | null  (used for distance ordering)
 *   focus           string     (businessId — included even if outside bbox)
 *   q               string     (free-text search across business + listings)
 *   spaceTypes      string[]
 *   employmentTypes string[]
 *   eligibleJobTypes string[]
 *
 * Returns `{ businesses, query }` where each business carries its matching
 * `spaces`, `jobs`, `classes` arrays (possibly empty — a business with a
 * lat/lng but no matching items still shows up as a "lone" pin).
 */
export const searchMap = async ({
  bbox,
  kinds,
  center,
  focus,
  q,
  spaceTypes,
  employmentTypes,
  eligibleJobTypes,
} = {}) => {
  const {token} = getToken()
  const params = new URLSearchParams()
  if (bbox) {
    params.set(
      'bbox',
      `${bbox.swLat},${bbox.swLng},${bbox.neLat},${bbox.neLng}`
    )
  }
  if (Array.isArray(kinds) && kinds.length > 0) {
    params.set('kinds', kinds.join(','))
  }
  if (center && Number.isFinite(center.lat) && Number.isFinite(center.lng)) {
    params.set('center', `${center.lat},${center.lng}`)
  }
  if (focus) params.set('focus', focus)
  if (q) params.set('q', q)
  if (Array.isArray(spaceTypes) && spaceTypes.length > 0) {
    params.set('spaceTypes', spaceTypes.join(','))
  }
  if (Array.isArray(employmentTypes) && employmentTypes.length > 0) {
    params.set('employmentTypes', employmentTypes.join(','))
  }
  if (Array.isArray(eligibleJobTypes) && eligibleJobTypes.length > 0) {
    params.set('eligibleJobTypes', eligibleJobTypes.join(','))
  }
  const response = await axios(proxy + `/map?${params.toString()}`, {
    method: 'GET',
    headers: token ? {Authorization: `Bearer ${token}`} : {},
  })
  if (response && response.data) {
    return response.data
  }
  return {businesses: [], query: null}
}

// ---------------------------------------------------------------------------
// Posts
// ---------------------------------------------------------------------------

/**
 * Create a post. The backend derives the author from the authenticated user;
 * businessId (optional) attaches the post to the user's acting-as business.
 *
 * @param {object} params
 * @param {'text'|'media'|'class_share'|'job_share'} params.type
 * @param {string} [params.body]
 * @param {string|null} [params.businessId]
 * @param {string[]} [params.mediaIds]  profile_media IDs to attach
 * @param {string|null} [params.classPostId]
 * @param {string|null} [params.jobPostId]
 */
export const createPost = async (params) => {
  const {token} = getToken()
  const response = await axios(proxy + '/posts', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: params,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

/**
 * Single post, in the same shape the chronological feed returns (author,
 * business, counts, media, embedded share payload). Backs the /post/:postId
 * permalink page. Token-optional on the backend, so the header is only sent
 * when we have one — a logged-out viewer gets isLiked/isFavorite false.
 */
export const getPost = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/posts/${id}`, {
    method: 'GET',
    headers: token ? {Authorization: `Bearer ${token}`} : {},
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const deletePost = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/posts/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const likePost = async (id, {businessId = null} = {}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/posts/${id}/likes`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {businessId},
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const unlikePost = async (id, {businessId = null} = {}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/posts/${id}/likes`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {businessId},
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const postPostComment = async (id, params) => {
  const {token} = getToken()
  const response = await axios(proxy + `/posts/${id}/comments`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: params,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getPostComments = async (id, pageNumber, size) => {
  const {token} = getToken()
  const from = Math.max(0, (pageNumber - 1) * size)
  // Token-optional: the backend serves comments to logged-out readers of a
  // public post permalink; sending "Bearer undefined" would be rejected.
  const response = await axios(
    proxy + `/posts/${id}/comments?from=${from}&size=${size}`,
    {
      method: 'GET',
      headers: token ? {Authorization: `Bearer ${token}`} : {},
    }
  )
  if (response && response.data) {
    const {comments, totalCount} = response.data
    return {comments, totalCount}
  }
  return null
}

/**
 * Public read of the chronological feed for the logged-out home page.
 * The backend route is token-optional; without a token isLiked/isFavorite
 * come back false. Never throws — a failed call just empties the section.
 */
export const getChronologicalFeedPublic = async (pageNumber = 1, size = 24) => {
  try {
    const from = pageNumber * size - size + 1
    const response = await axios(
      proxy + `/feed/chronological?from=${from}&size=${size}`,
      {method: 'GET'}
    )
    const {posts, totalCount} = response?.data || {}
    return {posts: posts || [], totalCount: totalCount || 0}
  } catch (error) {
    return {posts: [], totalCount: 0}
  }
}

/**
 * The authenticated feed, cursor-paged. Pass the `nextCursor` from the
 * previous page (empty for the first) and the visitor's anonymousId so
 * the server fills sponsored slots the same way on a refetch. Returns
 * organic posts plus the placements (featured and sponsored posts) the
 * server slotted into this page; see pages/Home/buildFeedDisplayList.
 * The connections scope never carries placements.
 *
 * `tag` narrows it to the posts carrying that hashtag (the tag page): no
 * placements, and the first page also returns `totalCount`.
 */
export const getChronologicalFeed = async ({
  cursor = '',
  size = 10,
  anonymousId = null,
  scope = null,
  tag = null,
} = {}) => {
  const {token} = getToken()
  const params = new URLSearchParams({cursor: cursor || '', size: String(size)})
  if (anonymousId) params.set('anonymousId', anonymousId)
  // 'connections' narrows the feed to people the member is connected to
  // (the home feed's "My connections" toggle). Left off for everyone.
  if (scope) params.set('scope', scope)
  if (tag) params.set('tag', tag)
  const response = await axios(proxy + `/feed/chronological?${params}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {posts, placements, nextCursor, hasMore, totalCount} = response.data
    return {
      posts: posts || [],
      placements: placements || [],
      nextCursor: nextCursor || null,
      hasMore: Boolean(hasMore),
      totalCount: totalCount ?? null,
    }
  }
  return null
}

// What the member can feature: {posts, jobs, spaces}. `posts` are their
// own (and their businesses') posts newest first, `isFeatured` flagging
// any already in a featured slot; `jobs` and `spaces` are their open
// listings that have no share post in the feed yet.
export const getMyPosts = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/posts/mine', {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  const data = response?.data || {}
  return {
    posts: data.posts || [],
    jobs: data.jobs || [],
    spaces: data.spaces || [],
  }
}

/**
 * One profile's posts: `authorId` for what a person posted as themselves,
 * `businessId` for what was posted as a business. Token-optional like the
 * public feed, because profiles are public by link.
 *
 * A scoped answer carries `likesTotal` (likes summed over every post in the
 * scope). An older server ignores the scope and answers with the global
 * feed, without that field, so `supported` is false and the caller shows no
 * posts rather than everyone's.
 */
export const getProfilePosts = async ({
  authorId,
  businessId,
  pageNumber = 1,
  size = 10,
}) => {
  const {token} = getToken()
  const from = pageNumber * size - size + 1
  const scope = businessId
    ? `businessId=${encodeURIComponent(businessId)}`
    : `authorId=${encodeURIComponent(authorId)}`
  const response = await axios(
    proxy + `/feed/chronological?from=${from}&size=${size}&${scope}`,
    {
      method: 'GET',
      headers: token ? {Authorization: `Bearer ${token}`} : {},
    }
  )
  const {posts, totalCount, likesTotal} = response?.data || {}
  const supported = likesTotal !== undefined && likesTotal !== null
  return {
    posts: supported ? posts || [] : [],
    totalCount: supported ? Number(totalCount) || 0 : 0,
    likesTotal: supported ? Number(likesTotal) || 0 : 0,
    supported,
  }
}

/**
 * A business's open roles, for the hiring chip and the Jobs tab. An older
 * server ignores `businessId` and sends every open job on the site; only an
 * answer that echoes the scope back is trusted.
 */
export const getBusinessOpenJobs = async (businessId, size = 20) => {
  const {token} = getToken()
  try {
    const response = await axios(
      proxy +
        `/jobs/posts/active/all?from=1&size=${size}&businessId=${encodeURIComponent(
          businessId
        )}`,
      {
        method: 'GET',
        headers: token ? {Authorization: `Bearer ${token}`} : {},
      }
    )
    const data = response?.data || {}
    if (data.businessId !== businessId) return {jobPosts: [], totalCount: 0}
    return {
      jobPosts: data.jobPosts || [],
      totalCount: Number(data.totalCount) || 0,
    }
  } catch (_) {
    return {jobPosts: [], totalCount: 0}
  }
}

// A business's upcoming classes, for its profile's Classes tab. Same contract
// as getBusinessOpenJobs: an older API that ignores businessId (no echo) reads
// as none rather than every class on the site.
export const getBusinessUpcomingClasses = async (businessId, size = 20) => {
  const {token} = getToken()
  try {
    const response = await axios(
      proxy +
        `/classes/posts/active/all?from=1&size=${size}&sortBy=date&sortOrder=ASC&businessId=${encodeURIComponent(
          businessId
        )}`,
      {
        method: 'GET',
        headers: token ? {Authorization: `Bearer ${token}`} : {},
      }
    )
    const data = response?.data || {}
    if (data.businessId !== businessId) return {classPosts: [], totalCount: 0}
    return {
      classPosts: data.classPosts || [],
      totalCount: Number(data.totalCount) || 0,
    }
  } catch (_) {
    return {classPosts: [], totalCount: 0}
  }
}

// A business's open spaces, for its profile's Spaces tab. Same contract as
// getBusinessOpenJobs.
export const getBusinessOpenSpaces = async (businessId, size = 20) => {
  const {token} = getToken()
  try {
    const response = await axios(
      proxy +
        `/spaces/posts/active/all?from=1&size=${size}&businessId=${encodeURIComponent(
          businessId
        )}`,
      {
        method: 'GET',
        headers: token ? {Authorization: `Bearer ${token}`} : {},
      }
    )
    const data = response?.data || {}
    if (data.businessId !== businessId) return {items: [], totalCount: 0}
    return {
      items: data.items || [],
      totalCount: Number(data.totalCount) || 0,
    }
  } catch (_) {
    return {items: [], totalCount: 0}
  }
}

/**
 * Role-aware Discover stream. `types` (array) and `professionScope`
 * ('mine'|'all') override the server's role defaults — omit both to get
 * them. Response: {cards, totalCount, scope} where scope echoes what the
 * server actually applied (drives the scope sheet).
 */
export const getDiscover = async (
  pageNumber,
  size,
  {types, professionScope, businessId} = {}
) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  let url = proxy + `/discover?from=${from + 1}&size=${size}`
  if (businessId) url += `&businessId=${businessId}`
  if (Array.isArray(types) && types.length > 0)
    url += `&types=${types.join(',')}`
  if (professionScope) url += `&professionScope=${professionScope}`
  const response = await axios(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    const {cards, totalCount, scope} = response.data
    return {cards, totalCount, scope}
  }
  return null
}

export const getSavedPosts = async (pageNumber, size) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  const response = await axios(
    proxy + `/favorites/portfolios?from=${from + 1}&size=${size}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    const {favorites, totalCount} = response.data
    return {favorites, totalCount}
  }
  return null
}

export const savePost = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/favorites/portfolio/${id}/add`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const unsavePost = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/favorites/portfolio/${id}/remove`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const postComment = async (data) => {
  const {token} = getToken()
  const response = await axios(proxy + '/medias/portfolio/comments', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getComments = async (id, pageNumber, size) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  const response = await axios(
    proxy + `/medias/portfolio/${id}/comments?from=${from + 1}&size=${size}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    const {comments, totalCount} = response.data
    return {comments, totalCount}
  }
  return null
}

export const deleteComment = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/medias/portfolio/comments/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const markAlertAsRead = async (data) => {
  const {token} = getToken()
  const response = await axios(proxy + `/users/alertMessage`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getConsumersAvatars = async () => {
  const {token} = getToken()
  const response = await axios(proxy + `/users/avatar/consumers`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getDefaultAvatars = async ({pageNumber, size = 12}) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  const response = await axios(
    proxy + `/users/avatar/default?from=${from + 1}&size=${size}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const saveDefaultAvatar = async (data) => {
  const {token} = getToken()
  const response = await axios(proxy + `/users/avatar/default`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getDefaultJobImages = async ({pageNumber, size = 12}) => {
  const {token} = getToken()
  const from = pageNumber * size - size
  const response = await axios(
    proxy + `/jobs/posts/logo/default?from=${from + 1}&size=${size}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

export const saveDefaultJobImage = async (data) => {
  const {token} = getToken()
  const response = await axios(proxy + `/jobs/posts/logo/default`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const googleSignIn = async (data) => {
  const response = await axios(proxy + '/googleSignIn', {
    method: 'POST',
    data,
  })
  if (response && response.data) {
    setDataInStorage({name: 'user', data: response.data})
    return response.data
  }
  return null
}

// Business API endpoints

// Get user's businesses
/**
 * Contact Us form. Public: the footer link it replaces was reachable signed
 * out, so the header only goes when there is really a session — `Bearer
 * undefined` is rejected outright. The API mails the shared inbox with the
 * sender's address as reply-to; it never mails the address typed in.
 */
export const submitContactForm = async ({name, email, subject, message}) => {
  const {token} = getToken()
  const response = await axios(proxy + '/contact', {
    method: 'POST',
    headers: token ? {Authorization: `Bearer ${token}`} : undefined,
    data: {name, email, subject, message},
  })
  return response?.data || null
}

/**
 * SMS setting for the number on the signed-in user's profile:
 * {phoneNumber, smsEnabled, stoppedByReply, startNumber}. Turning texts on
 * is refused (409) while the number has replied STOP — only a START reply
 * from the phone lifts Twilio's block.
 */
export const getSmsPreferences = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/sms/preferences', {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  return response?.data || null
}

export const updateSmsPreferences = async ({smsEnabled}) => {
  const {token} = getToken()
  const response = await axios(proxy + '/sms/preferences', {
    method: 'PUT',
    headers: {Authorization: `Bearer ${token}`},
    data: {smsEnabled},
  })
  return response?.data || null
}

// SendGrid unsubscribe groups: {email, groups: [{id, name, description, subscribed}]}
export const getEmailPreferences = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/email/preferences', {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  return response?.data || null
}

export const updateEmailPreferences = async ({groupId, subscribed}) => {
  const {token} = getToken()
  const response = await axios(proxy + '/email/preferences', {
    method: 'PUT',
    headers: {Authorization: `Bearer ${token}`},
    data: {groupId, subscribed},
  })
  return response?.data || null
}

export const getUserBusinesses = async (userId) => {
  const {token} = getToken()
  const response = await axios(proxy + `/users/${userId}/businesses`, {
    method: 'GET',
    // Header only when there is a token: `Bearer undefined` is rejected
    // outright, so a signed-out visitor saw none of an owner's businesses.
    headers: token ? {Authorization: `Bearer ${token}`} : undefined,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

// Get business by ID
export const getBusiness = async (businessId) => {
  const {token} = getToken()
  const response = await axios(proxy + `/businesses/${businessId}`, {
    method: 'GET',
    // Token-optional: /businesses/:id is authenticateOrPublic, so logged-out
    // viewers of public space/business pages can read it. Omit the header
    // when signed out rather than sending "Bearer undefined".
    headers: token ? {Authorization: `Bearer ${token}`} : {},
  })
  if (response && response.data) {
    // Some older businesses store socialMediaUrls as {} rather than a list,
    // and every reader of it expects a list.
    const {socialMediaUrls} = response.data
    return {
      ...response.data,
      socialMediaUrls: Array.isArray(socialMediaUrls) ? socialMediaUrls : [],
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// Business leads - inbound interest from (often logged-out) visitors.
// Public submit (token-optional); admin list/update are authenticated.
// ---------------------------------------------------------------------------
export const submitBusinessLead = async (businessId, payload) => {
  const {token} = getToken()
  const response = await axios(proxy + `/businesses/${businessId}/leads`, {
    method: 'POST',
    headers: token ? {Authorization: `Bearer ${token}`} : {},
    // anonymousId lets the backend attribute the business_lead touchpoint
    // to this visitor when they're logged out
    data: {...payload, anonymousId: getAnonymousId()},
  })
  if (response && response.data) return response.data
  return null
}

export const getAdminLeads = async ({status, limit = 100} = {}) => {
  const {token} = getToken()
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (limit) params.set('limit', String(limit))
  const response = await axios(proxy + `/admin/leads?${params.toString()}`, {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return {
    leads: [],
    counts: {new: 0, contacted: 0, converted: 0, dismissed: 0, total: 0},
  }
}

export const updateLeadStatus = async (id, status) => {
  const {token} = getToken()
  const response = await axios(proxy + `/admin/leads/${id}/status`, {
    method: 'PUT',
    headers: {Authorization: `Bearer ${token}`},
    data: {status},
  })
  if (response && response.data) return response.data
  return null
}

// Create business
export const createBusiness = async (businessData) => {
  const {token} = getToken()
  const response = await axios(proxy + '/businesses', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    data: businessData,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

// Update business
export const updateBusiness = async (businessId, updates) => {
  const {token} = getToken()
  const response = await axios(proxy + `/businesses/${businessId}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    data: updates,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

// Delete business
export const deleteBusiness = async (businessId) => {
  const {token} = getToken()
  const response = await axios(proxy + `/businesses/${businessId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

// Set primary business
export const setPrimaryBusiness = async (userId, businessId) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/users/${userId}/businesses/${businessId}/primary`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: {},
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

// Get business team
export const getBusinessTeam = async (businessId) => {
  const {token} = getToken()
  const response = await axios(proxy + `/businesses/${businessId}/team`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

// Add team member
export const addTeamMember = async (businessId, userId, role) => {
  const {token} = getToken()
  const response = await axios(proxy + `/businesses/${businessId}/team`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    data: {userId, role},
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

// Remove team member
export const removeTeamMember = async (businessId, userId) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/businesses/${businessId}/team/${userId}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (response && response.data) {
    return response.data
  }
  return null
}

// Assessment API functions
export const saveUserAssessment = async (data) => {
  const {token} = getToken()
  const response = await axios(proxy + '/assessments', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getUserAssessment = async (userId) => {
  const {token} = getToken()
  const headers = token ? {Authorization: `Bearer ${token}`} : {}
  const response = await axios(proxy + `/assessments/user/${userId}`, {
    method: 'GET',
    headers,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const saveBusinessAssessment = async (businessId, data) => {
  const {token} = getToken()
  const response = await axios(proxy + `/assessments/business/${businessId}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getBusinessAssessment = async (businessId) => {
  const {token} = getToken()
  const headers = token ? {Authorization: `Bearer ${token}`} : {}
  const response = await axios(proxy + `/assessments/business/${businessId}`, {
    method: 'GET',
    headers,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const searchAll = async (q, limit = 5) => {
  const {token} = getToken()
  const url = proxy + `/search?q=${encodeURIComponent(q)}&limit=${limit}`
  const response = await axios(url, {
    method: 'GET',
    headers: token ? {Authorization: `Bearer ${token}`} : {},
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

// --- Touchpoint analytics -------------------------------------------------
// Logs a client-side view/search event to POST /touchpoints. Fire-and-forget:
// tracking must never throw or slow down the UI, so every failure is
// swallowed. Works logged-out (anonymousId) and logged-in (Bearer token).
export const logTouchpoint = async (payload) => {
  try {
    const {token} = getToken()
    await axios(proxy + '/touchpoints', {
      method: 'POST',
      headers: token ? {Authorization: `Bearer ${token}`} : {},
      data: {...payload, anonymousId: getAnonymousId()},
    })
  } catch (error) {
    // tracking is best-effort — never surface errors to the user
  }
}

// All-time view count for a public entity — entityType is post | space | class.
// Public, so it works logged-out. Returns null on failure: a view counter must
// never break the page it decorates, and the label just hides itself.
export const getEntityViews = async ({entityType, entityId}) => {
  try {
    const response = await axios(proxy + '/touchpoints/views', {
      method: 'GET',
      params: {entityType, entityId},
    })
    if (response && response.data) return response.data
    return null
  } catch (error) {
    return null
  }
}

// Recent views for the caller's OWN profile or business, excluding
// their own visits. entityType is profile | business; days defaults to
// 7 server-side. Requires a token, and the server enforces ownership
// (403 for anyone else) — returns null for non-owners and errors.
export const getOwnerViews = async ({entityType, entityId, days}) => {
  try {
    const {token} = getToken()
    if (!token) return null
    const response = await axios(proxy + '/touchpoints/views/recent', {
      method: 'GET',
      headers: {Authorization: `Bearer ${token}`},
      params: {entityType, entityId, days},
    })
    if (response && response.data) return response.data
    return null
  } catch (error) {
    return null
  }
}

// Balance + recent ledger for the caller's own Sharpennies.
export const getCredits = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/credits', {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}

// Reward credits the user hasn't seen a popup for yet (one per block
// of coins awarded), and the ack that marks them shown.
export const getRewardNotifications = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/credits/reward-notifications', {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}

export const ackRewardNotifications = async (ids) => {
  const {token} = getToken()
  const response = await axios(proxy + '/credits/reward-notifications/ack', {
    method: 'POST',
    headers: {Authorization: `Bearer ${token}`},
    data: {ids},
  })
  if (response && response.data) return response.data
  return null
}

// Which reward triggers the signed-in member can still earn —
// {availability: {post_created: {available, amount}, ...}}. Drives the
// coin hints next to the post/comment buttons.
export const getRewardAvailability = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/credits/reward-availability', {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}

// ---------------------------------------------------------------------------
// Rewards catalog — the vendor-donated items members buy with Sharpennies.
// The trigger endpoints above pay coins in; these spend them.
//
// Backend contract:
//   GET  /rewards            → {balance, rewards: [{id, name, description,
//        imageUrl, businessId, businessName, userId, userName,
//        priceSharpennies, quantityOffered, quantityClaimed, maxPerUser,
//        claimedByMe, expiresAt, redemptionInstructions}]}
//        Live rewards only — draft/paused/archived never leave the admin.
//        Sold-out and expired ones DO come back: a store where nothing has
//        ever sold looks dead.
//   POST /rewards/:id/claim  → {claim, placement, balance}
//        Debits the coins and decrements stock in one transaction. 409 when
//        someone took the last one first, 402 when the balance moved. A
//        feature_post reward takes {postId} and returns the placement.
//   GET  /rewards/claims     → {claims: [...]} — the member's own claims.
// ---------------------------------------------------------------------------

export const getRewardsCatalog = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/rewards', {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}

// A feature_post reward takes exactly one of postId, jobPostId or
// spacePostId (the member's own post, or a listing featured through its
// share post) and is ignored otherwise. The response then also carries
// `placement` with the featured window's `endsAt`.
export const claimReward = async (
  rewardId,
  {postId, jobPostId, spacePostId} = {}
) => {
  const {token} = getToken()
  const target = postId
    ? {postId}
    : jobPostId
      ? {jobPostId}
      : spacePostId
        ? {spacePostId}
        : {}
  const response = await axios(proxy + `/rewards/${rewardId}/claim`, {
    method: 'POST',
    headers: {Authorization: `Bearer ${token}`},
    data: target,
  })
  if (response && response.data) return response.data
  return null
}

export const getMyRewardClaims = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/rewards/claims', {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}

// Business self-serve: a business owner's own rewards, the claims on
// them, and sponsored posts. Every endpoint checks server-side that the
// caller owns :businessId.
//   GET  /businesses/:id/rewards                    → {rewards}
//   POST /businesses/:id/rewards                    body reward + {submit}
//   PUT  /businesses/:id/rewards/:rewardId          body reward + {submit}
//   PUT  /businesses/:id/rewards/:rewardId/status   body {action: submit|withdraw|pause|resume|end}
//   GET  /businesses/:id/reward-claims              → {claims}
//   POST /businesses/:id/reward-claims/:claimId/fulfill  body {note}
//   GET  /businesses/:id/sponsored-posts/quote?days=N → {quote}
//   GET  /businesses/:id/sponsored-posts            → {orders}
//   POST /businesses/:id/sponsored-posts            body {postId|jobPostId|spacePostId, durationDays}
//        → {order, clientSecret} (clientSecret null for a free order)
//   POST /businesses/:id/sponsored-posts/:orderId/payment → {order, clientSecret}
const businessRequest = async (method, path, data) => {
  const {token} = getToken()
  const response = await axios(proxy + path, {
    method,
    headers: {Authorization: `Bearer ${token}`},
    ...(data !== undefined ? {data} : {}),
  })
  return response?.data ?? null
}

export const getBusinessRewards = (businessId) =>
  businessRequest('GET', `/businesses/${businessId}/rewards`)

export const createBusinessReward = ({businessId, reward}) =>
  businessRequest('POST', `/businesses/${businessId}/rewards`, reward)

export const updateBusinessReward = ({businessId, rewardId, reward}) =>
  businessRequest(
    'PUT',
    `/businesses/${businessId}/rewards/${rewardId}`,
    reward
  )

export const setBusinessRewardStatus = ({businessId, rewardId, action}) =>
  businessRequest(
    'PUT',
    `/businesses/${businessId}/rewards/${rewardId}/status`,
    {action}
  )

export const getBusinessRewardClaims = (businessId) =>
  businessRequest('GET', `/businesses/${businessId}/reward-claims`)

export const fulfillBusinessRewardClaim = ({businessId, claimId, note}) =>
  businessRequest(
    'POST',
    `/businesses/${businessId}/reward-claims/${claimId}/fulfill`,
    {note: note || null}
  )

export const getSponsoredPostQuote = ({businessId, days}) =>
  businessRequest(
    'GET',
    `/businesses/${businessId}/sponsored-posts/quote?days=${encodeURIComponent(days)}`
  )

export const getSponsoredPostOrders = (businessId) =>
  businessRequest('GET', `/businesses/${businessId}/sponsored-posts`)

export const createSponsoredPostOrder = ({businessId, target, durationDays}) =>
  businessRequest('POST', `/businesses/${businessId}/sponsored-posts`, {
    ...target,
    durationDays,
  })

export const resumeSponsoredPostPayment = ({businessId, orderId}) =>
  businessRequest(
    'POST',
    `/businesses/${businessId}/sponsored-posts/${orderId}/payment`
  )

// The coin packages on offer and what they cost, priced from the rate an
// admin set (Admin → Rewards). Served rather than hardcoded so the price
// shown is the price Stripe will charge.
//   → {baseRate, packages: [{amount, price, perCoin, discountPercent}]}
export const getCreditPackages = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/credits/packages', {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}

// Clears never-used conversations out of the caller's inbox — threads
// created by tapping "Message" and never written in. A per-member soft
// delete, so the other person's copy is untouched and the thread
// returns if either of them ever sends something. Called when the
// messages modal opens.
export const deleteEmptyConversations = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/conversations/empty', {
    method: 'DELETE',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}

/* ------------------------------------------------------------------ */
/* Admin moderation (Admin → Posts, and the admin menu on detail pages) */
/* ------------------------------------------------------------------ */

/**
 * Feed posts for the admin Posts tab — includes inactive (disabled or
 * author-deleted) posts, newest first. `search` matches body, author and
 * business name on the server; the page also filters client-side.
 */
export const getAdminPosts = async ({
  from = 1,
  size = 200,
  search = '',
} = {}) => {
  const {token} = getToken()
  const params = new URLSearchParams({from: String(from), size: String(size)})
  if (search) params.set('search', search)
  const response = await axios(proxy + `/admin/posts?${params.toString()}`, {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}

// One shape for every "activate / disable" admin toggle: PUT
// /admin/<kind>/:id/activate with {active}. Disabling hides the entity
// from public surfaces; activating brings it back.
const adminSetActive = (kind) => async (id, active) => {
  const {token} = getToken()
  const response = await axios(proxy + `/admin/${kind}/${id}/activate`, {
    method: 'PUT',
    headers: {Authorization: `Bearer ${token}`},
    data: {active: Boolean(active)},
  })
  if (response && response.data) return response.data
  return null
}

export const adminSetPostActive = adminSetActive('posts')
export const adminSetJobActive = adminSetActive('jobs')
export const adminSetClassActive = adminSetActive('classes')
export const adminSetSpaceActive = adminSetActive('spaces')

/**
 * Admin hard-delete of a feed post. The author-facing deletePost is a soft
 * delete (the post can be re-activated from Admin → Posts); this one removes
 * the row and its likes / comments for good.
 */
export const adminDeletePost = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/admin/posts/${id}`, {
    method: 'DELETE',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}

/** Delete a class post — owner, or admin (the backend bypasses ownership). */
export const deleteClassPost = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/classes/posts/${id}`, {
    method: 'DELETE',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}

/**
 * Delete a feed-post comment (and its replies). Allowed for the comment's
 * author and for admins. Portfolio-media comments use deleteComment.
 */
export const deletePostComment = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/posts/comments/${id}`, {
    method: 'DELETE',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}

// ---------------------------------------------------------------------------
// Sharpets — the member's companion pet. Every endpoint answers
// {sharpet, treatCost}, with sharpet null before adoption, so any response
// can replace the cached one; a treat also returns the new coin balance.
// ---------------------------------------------------------------------------

export const getMySharpet = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/sharpets/me', {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}

export const adoptSharpet = async ({name, species}) => {
  const {token} = getToken()
  const response = await axios(proxy + '/sharpets', {
    method: 'POST',
    headers: {Authorization: `Bearer ${token}`},
    data: {name, species},
  })
  if (response && response.data) return response.data
  return null
}

export const renameSharpet = async (name) => {
  const {token} = getToken()
  const response = await axios(proxy + '/sharpets/me', {
    method: 'PATCH',
    headers: {Authorization: `Bearer ${token}`},
    data: {name},
  })
  if (response && response.data) return response.data
  return null
}

export const giveSharpetTreat = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/sharpets/me/treats', {
    method: 'POST',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}

// Admin: every app setting with its stored value or default, and saving
// one. The server allowlists keys and checks the value's type and range.
export const getAdminAppSettings = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/admin/app-settings', {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  return response?.data?.settings || {}
}

export const updateAdminAppSetting = async ({key, value}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/admin/app-settings/${key}`, {
    method: 'PUT',
    headers: {Authorization: `Bearer ${token}`},
    data: {value},
  })
  return response?.data
}

// Turns the pet into another species for speciesChangeCost Sharpennies;
// also returns the new coin balance.
export const changeSharpetSpecies = async (species) => {
  const {token} = getToken()
  const response = await axios(proxy + '/sharpets/me/species', {
    method: 'PUT',
    headers: {Authorization: `Bearer ${token}`},
    data: {species},
  })
  if (response && response.data) return response.data
  return null
}

// The recent chat thread with the member's Sharpet, oldest first.
export const getSharpetMessages = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/sharpets/me/messages', {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}

// Says something to the pet. The reply comes back in the same response,
// with the pet's new state: {sharpet, treatCost, chatEnergyCost, messages}.
export const sendSharpetMessage = async (content) => {
  const {token} = getToken()
  const response = await axios(proxy + '/sharpets/me/messages', {
    method: 'POST',
    headers: {Authorization: `Bearer ${token}`},
    data: {content},
  })
  if (response && response.data) return response.data
  return null
}

// ---------------------------------------------------------------------------
// Connections: mutual links between people (not businesses, which keep
// favorites). Every write answers like the status read, {status,
// connectionCount}, where status is the pair seen from the signed-in member
// ('none' | 'outgoing' | 'incoming' | 'connected') and connectionCount is
// the other person's, so a response can go straight into the profile
// button's cache.
// ---------------------------------------------------------------------------

export const getConnectionStatus = async (userId) => {
  const {token} = getToken()
  const response = await axios(proxy + `/connections/status/${userId}`, {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  return response?.data || null
}

// Asks to connect; if they already asked you, this accepts instead.
export const sendConnectionRequest = async (userId) => {
  const {token} = getToken()
  const response = await axios(proxy + `/connections/${userId}`, {
    method: 'POST',
    headers: {Authorization: `Bearer ${token}`},
  })
  return response?.data || null
}

export const acceptConnectionRequest = async (userId) => {
  const {token} = getToken()
  const response = await axios(proxy + `/connections/${userId}/accept`, {
    method: 'POST',
    headers: {Authorization: `Bearer ${token}`},
  })
  return response?.data || null
}

// Cancels a request you sent, ignores one you got, or removes a
// connection. Nobody is told.
export const removeConnection = async (userId) => {
  const {token} = getToken()
  const response = await axios(proxy + `/connections/${userId}`, {
    method: 'DELETE',
    headers: {Authorization: `Bearer ${token}`},
  })
  return response?.data || null
}

// One page of a person's connections (yours when userId is left out),
// newest first: {connections, nextCursor, hasMore, totalCount}.
export const getConnections = async ({userId, cursor = '', size = 20} = {}) => {
  const {token} = getToken()
  const params = new URLSearchParams({cursor: cursor || '', size: String(size)})
  if (userId) params.set('userId', userId)
  const response = await axios(proxy + `/connections?${params}`, {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  const data = response?.data || {}
  return {
    connections: data.connections || [],
    nextCursor: data.nextCursor || null,
    hasMore: Boolean(data.hasMore),
    totalCount: Number(data.totalCount) || 0,
  }
}

// Your pending requests both ways, with counts, and your own connection
// count: {incoming, outgoing, incomingCount, outgoingCount, connectionCount}.
export const getConnectionRequests = async () => {
  const {token} = getToken()
  const response = await axios(proxy + '/connections/requests', {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  const data = response?.data || {}
  return {
    incoming: data.incoming || [],
    outgoing: data.outgoing || [],
    incomingCount: Number(data.incomingCount) || 0,
    outgoingCount: Number(data.outgoingCount) || 0,
    connectionCount: Number(data.connectionCount) || 0,
  }
}

// Someone else's Sharpet, from their profile. Every profile has one (the
// default Shar-Pei before its member adopts). Answers {sharpet: {name,
// displayName, species, isDefault, mood, ownerFirstName}, visitor,
// treatCost, chatEnergyCost, chatCoinCost}; visitor is the caller's own
// energy with this pet, null when signed out. Works signed out.
export const getProfileSharpet = async (userId) => {
  const {token} = getToken()
  const response = await axios(proxy + `/sharpets/users/${userId}`, {
    method: 'GET',
    headers: token ? {Authorization: `Bearer ${token}`} : {},
  })
  if (response && response.data) return response.data
  return null
}

// The caller's private thread with someone else's pet, oldest first.
export const getProfileSharpetMessages = async (userId) => {
  const {token} = getToken()
  const response = await axios(proxy + `/sharpets/users/${userId}/messages`, {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}

// Says something to someone else's pet: the profile shape plus messages
// (and balance, when chat is priced).
export const sendProfileSharpetMessage = async ({userId, content}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/sharpets/users/${userId}/messages`, {
    method: 'POST',
    headers: {Authorization: `Bearer ${token}`},
    data: {content},
  })
  if (response && response.data) return response.data
  return null
}

// A treat that tops up the caller's energy with someone else's pet; also
// returns the new coin balance.
export const giveProfileSharpetTreat = async (userId) => {
  const {token} = getToken()
  const response = await axios(proxy + `/sharpets/users/${userId}/treats`, {
    method: 'POST',
    headers: {Authorization: `Bearer ${token}`},
  })
  if (response && response.data) return response.data
  return null
}
